import React, { useState } from 'react';

interface RegexExample {
  name: string;
  pattern: string;
  description: string;
  examples: string[];
  category: string;
}

const RegexTool: React.FC = () => {
  const [regexPattern, setRegexPattern] = useState('');
  const [testText, setTestText] = useState('');
  const [flags, setFlags] = useState({
    global: true,
    ignoreCase: false,
    multiline: false,
    unicode: false,
    sticky: false,
    dotAll: false
  });
  const [matches, setMatches] = useState<string[][]>([]);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<'test' | 'examples' | 'help'>('test');
  const [selectedExample, setSelectedExample] = useState<RegexExample | null>(null);

  // 常用正则表达式示例
  const regexExamples: RegexExample[] = [
    {
      name: '邮箱地址',
      pattern: '^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$',
      description: '匹配标准的邮箱地址格式',
      examples: ['user@example.com', 'test.email+tag@domain.co.uk', 'invalid-email'],
      category: '常用验证'
    },
    {
      name: '手机号码',
      pattern: '^1[3-9]\\d{9}$',
      description: '匹配中国大陆手机号码（11位数字，以1开头）',
      examples: ['13800138000', '18612345678', '12345678901'],
      category: '常用验证'
    },
    {
      name: '中文字符',
      pattern: '[\\u4e00-\\u9fa5]',
      description: '匹配任意中文字符',
      examples: ['你好世界', 'Hello World', '123'],
      category: '文本匹配'
    },
    {
      name: '数字',
      pattern: '\\d+',
      description: '匹配一个或多个数字',
      examples: ['123', 'abc123def', 'no-numbers'],
      category: '文本匹配'
    }
  ];

  // 测试正则表达式
  const testRegex = () => {
    if (!regexPattern.trim()) {
      setError('请输入正则表达式');
      setMatches([]);
      return;
    }

    if (!testText.trim()) {
      setError('请输入测试文本');
      setMatches([]);
      return;
    }

    try {
      setError('');
      
      // 构建标志字符串
      let flagsStr = '';
      if (flags.global) flagsStr += 'g';
      if (flags.ignoreCase) flagsStr += 'i';
      if (flags.multiline) flagsStr += 'm';
      if (flags.unicode) flagsStr += 'u';
      if (flags.sticky) flagsStr += 'y';
      if (flags.dotAll) flagsStr += 's';

      const regex = new RegExp(regexPattern, flagsStr);
      const allMatches: string[][] = [];
      
      if (flags.global) {
        // 全局匹配 - 使用 match 获取所有匹配项
        const matches = testText.match(regex);
        if (matches) {
          // 将每个匹配项作为单独的数组元素
          matches.forEach(match => {
            allMatches.push([match]);
          });
        }
      } else {
        // 非全局匹配
        const match = testText.match(regex);
        if (match) {
          allMatches.push([...match]);
        }
      }

      setMatches(allMatches);
    } catch (err) {
      setError(`正则表达式错误: ${err instanceof Error ? err.message : '无效的正则表达式'}`);
      setMatches([]);
    }
  };

  // 加载示例
  const loadExample = (example: RegexExample) => {
    setRegexPattern(example.pattern);
    setTestText(example.examples.join('\n'));
    setSelectedExample(example);
    setActiveTab('test');
  };

  // 复制到剪贴板
  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  // 清空输入
  const handleClear = () => {
    setRegexPattern('');
    setTestText('');
    setMatches([]);
    setError('');
    setSelectedExample(null);
  };

  // 获取标志说明
  const getFlagDescription = (flag: string) => {
    const descriptions: { [key: string]: string } = {
      global: 'g - 全局匹配，查找所有匹配项',
      ignoreCase: 'i - 忽略大小写',
      multiline: 'm - 多行模式，^和$匹配行首行尾',
      unicode: 'u - Unicode模式，支持Unicode属性转义',
      sticky: 'y - 粘性匹配，从lastIndex开始匹配',
      dotAll: 's - 点号匹配所有字符，包括换行符'
    };
    return descriptions[flag] || '';
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-800 mb-4">正则表达式工具</h1>
          <p className="text-lg text-gray-600">测试正则表达式、查看常用示例、学习正则语法</p>
        </div>

        {/* Tab Navigation */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex space-x-1">
            <button
              onClick={() => setActiveTab('test')}
              className={`px-6 py-3 rounded-lg font-medium transition-colors ${
                activeTab === 'test'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              🧪 正则测试
            </button>
            <button
              onClick={() => setActiveTab('examples')}
              className={`px-6 py-3 rounded-lg font-medium transition-colors ${
                activeTab === 'examples'
                  ? 'bg-blue-600 text-white'
                  : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
              }`}
            >
              📚 常用示例
            </button>
            <button
              onClick={() => setActiveTab('help')}
              className={`px-6 py-3 rounded-lg font-medium transition-colors ${
                activeTab === 'help'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              ❓ 使用帮助
            </button>
          </div>
        </div>

        {/* Regex Testing Tab */}
        {activeTab === 'test' && (
          <div className="space-y-6">
            {/* Regex Pattern Input */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-800">正则表达式</h2>
                <div className="flex space-x-2">
                  <button
                    onClick={handleClear}
                    className="px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
                  >
                    清空
                  </button>
                  <button
                    onClick={testRegex}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium"
                  >
                    测试匹配
                  </button>
                </div>
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  正则表达式模式
                </label>
                <input
                  type="text"
                  value={regexPattern}
                  onChange={(e) => setRegexPattern(e.target.value)}
                  placeholder="输入正则表达式，如: ^[a-z]+$"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                />
              </div>

              {/* Flags */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  匹配标志
                </label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {Object.entries(flags).map(([key, value]) => (
                    <label key={key} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={value}
                        onChange={(e) => setFlags(prev => ({ ...prev, [key]: e.target.checked }))}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">
                        {key.charAt(0).toUpperCase() + key.slice(1)}
                      </span>
                      <span className="text-xs text-gray-500" title={getFlagDescription(key)}>
                        ({key === 'global' ? 'g' : key === 'ignoreCase' ? 'i' : key === 'multiline' ? 'm' : key === 'unicode' ? 'u' : key === 'sticky' ? 'y' : 's'})
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {selectedExample && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-sm font-medium text-blue-800">已加载示例: {selectedExample.name}</span>
                      <p className="text-xs text-blue-600 mt-1">{selectedExample.description}</p>
                    </div>
                    <button
                      onClick={() => setSelectedExample(null)}
                      className="text-blue-600 hover:text-blue-800 text-sm"
                    >
                      ✕
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Test Text Input */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  测试文本
                </label>
                <textarea
                  value={testText}
                  onChange={(e) => setTestText(e.target.value)}
                  placeholder="输入要测试的文本..."
                  className="w-full h-32 p-3 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                />
              </div>

              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-700 text-sm">{error}</p>
                </div>
              )}
            </div>

            {/* Results */}
            {matches.length > 0 && (
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">
                  匹配结果 ({matches.length} 个)
                </h3>
                
                <div className="p-4 bg-gray-50 rounded-lg border">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-medium text-gray-700">所有匹配项:</span>
                    <button
                      onClick={() => handleCopy(matches.map(m => m[0]).join(', '))}
                      className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors"
                    >
                      复制全部
                    </button>
                  </div>
                  
                  <div className="text-sm">
                    {matches.map((match, index) => (
                      <span key={index}>
                        {match[0]}
                        {index < matches.length - 1 && ' '}
                      </span>
                    ))}
                  </div>
                  
                  {matches.some(match => match.length > 1) && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <span className="text-xs text-gray-500">捕获组详情:</span>
                      <div className="mt-2 space-y-1">
                        {matches.map((match, index) => 
                          match.length > 1 && (
                            <div key={index} className="text-xs">
                              <span className="text-gray-600">匹配 #{index + 1}:</span>
                              {match.slice(1).map((group, groupIndex) => (
                                <span key={groupIndex} className="ml-2 font-mono bg-green-100 px-1 py-0.5 rounded">
                                  组{groupIndex + 1}: {group}
                                </span>
                              ))}
                            </div>
                          )
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Examples Tab */}
        {activeTab === 'examples' && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-6">常用正则表达式示例</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {regexExamples.map((example) => (
                <div key={example.name} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="text-lg font-semibold text-gray-800">{example.name}</h3>
                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                      {example.category}
                    </span>
                  </div>
                  
                  <p className="text-sm text-gray-600 mb-3">{example.description}</p>
                  
                  <div className="mb-3">
                    <label className="block text-xs font-medium text-gray-700 mb-1">正则表达式:</label>
                    <div className="flex items-center space-x-2">
                      <code className="flex-1 p-2 bg-gray-100 rounded text-xs font-mono break-all">
                        {example.pattern}
                      </code>
                      <button
                        onClick={() => handleCopy(example.pattern)}
                        className="px-2 py-1 text-xs bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors"
                        title="复制正则表达式"
                      >
                        复制
                      </button>
                    </div>
                  </div>
                  
                  <div className="mb-3">
                    <label className="block text-xs font-medium text-gray-700 mb-1">测试示例:</label>
                    <div className="space-y-1">
                      {example.examples.map((ex, index) => (
                        <div key={index} className="text-xs font-mono bg-gray-50 px-2 py-1 rounded">
                          {ex}
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <button
                    onClick={() => loadExample(example)}
                    className="w-full px-3 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors"
                  >
                    使用此示例
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Help Tab */}
        {activeTab === 'help' && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-6">正则表达式使用帮助</h2>
            
            <div className="space-y-8">
              {/* Basic Syntax */}
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-4">基础语法</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-3">
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <h4 className="font-medium text-gray-800 mb-2">字符类</h4>
                      <div className="text-sm space-y-1">
                        <div><code className="bg-gray-200 px-1 rounded">[abc]</code> - 匹配a、b或c中的任意一个</div>
                        <div><code className="bg-gray-200 px-1 rounded">[^abc]</code> - 匹配除了a、b、c以外的任意字符</div>
                        <div><code className="bg-gray-200 px-1 rounded">[a-z]</code> - 匹配a到z之间的任意小写字母</div>
                        <div><code className="bg-gray-200 px-1 rounded">[0-9]</code> - 匹配0到9之间的任意数字</div>
                      </div>
                    </div>
                    
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <h4 className="font-medium text-gray-800 mb-2">预定义字符类</h4>
                      <div className="text-sm space-y-1">
                        <div><code className="bg-gray-200 px-1 rounded">\d</code> - 匹配任意数字，等价于[0-9]</div>
                        <div><code className="bg-gray-200 px-1 rounded">\w</code> - 匹配字母、数字、下划线，等价于[a-zA-Z0-9_]</div>
                        <div><code className="bg-gray-200 px-1 rounded">\s</code> - 匹配任意空白字符</div>
                        <div><code className="bg-gray-200 px-1 rounded">\D</code> - 匹配任意非数字字符</div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <h4 className="font-medium text-gray-800 mb-2">量词</h4>
                      <div className="text-sm space-y-1">
                        <div><code className="bg-gray-200 px-1 rounded">*</code> - 匹配前面的元素0次或多次</div>
                        <div><code className="bg-gray-200 px-1 rounded">+</code> - 匹配前面的元素1次或多次</div>
                        <div><code className="bg-gray-200 px-1 rounded">?</code> - 匹配前面的元素0次或1次</div>
                        <div><code className="bg-gray-200 px-1 rounded">&#123;n&#125;</code> - 匹配前面的元素恰好n次</div>
                      </div>
                    </div>
                    
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <h4 className="font-medium text-gray-800 mb-2">锚点</h4>
                      <div className="text-sm space-y-1">
                        <div><code className="bg-gray-200 px-1 rounded">^</code> - 匹配字符串开始</div>
                        <div><code className="bg-gray-200 px-1 rounded">$</code> - 匹配字符串结束</div>
                        <div><code className="bg-gray-200 px-1 rounded">\b</code> - 匹配单词边界</div>
                        <div><code className="bg-gray-200 px-1 rounded">\B</code> - 匹配非单词边界</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tips */}
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-4">使用技巧</h3>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <ul className="text-blue-800 space-y-2 text-sm">
                    <li>• 使用在线工具测试正则表达式，确保匹配结果符合预期</li>
                    <li>• 注意转义字符，特殊字符前需要加反斜杠</li>
                    <li>• 合理使用捕获组，可以提取匹配内容中的特定部分</li>
                    <li>• 避免过度复杂的正则表达式，可读性很重要</li>
                    <li>• 测试边界情况，确保正则表达式的健壮性</li>
                    <li>• 使用适当的标志，如忽略大小写、全局匹配等</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Features */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-lg shadow-md p-6 text-center">
            <div className="text-3xl mb-3">🧪</div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">实时测试</h3>
            <p className="text-gray-600">输入正则表达式和测试文本，实时查看匹配结果</p>
          </div>
          
          <div className="bg-white rounded-lg shadow-md p-6 text-center">
            <div className="text-3xl mb-3">📚</div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">丰富示例</h3>
            <p className="text-gray-600">提供常用正则表达式示例，快速上手使用</p>
          </div>
          
          <div className="bg-white rounded-lg shadow-md p-6 text-center">
            <div className="text-3xl mb-3">❓</div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">详细帮助</h3>
            <p className="text-gray-600">包含语法说明、使用技巧和最佳实践</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegexTool;
